import clsx from 'clsx'
import delay from 'lodash/delay'
import map from 'lodash/map'
import React, { useEffect, useState } from 'react'

import SvgCheck from '@Assets/SVG/Check'
import SvgPlus from '@Assets/SVG/Plus'
import { BuyButtonV2 } from '@Components/BuyButton/v2'
import { Chip } from '@Components/Chips/Chip/Chip'
import { ProgressLineIndicator } from '@CoreComponents/Progress/ProgressLineIndicator'
import { CartV2LineItem } from '@Domains/Cart/Feature/CartV2/CartV2'
import { ProductItem } from '@Domains/Product/Components/ProductItem'
import { AddToCartPromptSuccessTacticFragmentFragment } from '@Generated/graphql'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'
import { createAddToCartTacticFromOffer } from '@Shared/Utilities/AddToCartHelper'

import styles from './AddAndSaveTactic.module.scss'

type Tactic = Extract<AddToCartPromptSuccessTacticFragmentFragment, { __typename: 'TacticAddAndSave' }>
type TacticOffer = NonNullable<Tactic['offers']>[0]

export const AddAndSaveTactic = ({
  lineItem,
  showProgressBar,
  tactic,
}: {
  lineItem: CartV2LineItem
  showProgressBar?: boolean
  tactic?: Tactic
}) => {
  const { t, tDanger } = useTranslation()
  const campaignOverrideV1 = useSplitTest('campaign_override_v1', 'b')
  const [addedToCart, setAddedToCart] = useState<TacticOffer | null>(null)
  const [showSuccess, setShowSuccess] = useState<boolean>(false)
  useEffect(() => {
    if (addedToCart && !showSuccess) {
      delay(() => setShowSuccess(true), 600)
    }
  }, [addedToCart, showSuccess])

  const percentage = tactic?.offers?.[0]?.offer?.price?.discount?.percent || 20

  return (
    <div className={clsx('fullwidth', styles.wrapper, campaignOverrideV1 && styles.campaignOverride)}>
      <div className={clsx(styles.header, showProgressBar && styles.showProgressBar)}>
        {campaignOverrideV1 && <Chip color="valentine_b_small" />}
        {!showProgressBar
          ? (
            <>
              <div className={styles.progressHeader}>{t('products:SAVE_ON_THE_NEXT')}</div>
              {!showSuccess && (
                <div className={styles.progressLineWrapper}>
                  <ProgressLineIndicator progress={!addedToCart ? 50 : 100} showEndIndicator v2 />
                  <span>{t('products:GET_X_OFF', { percentage: 20 })}</span>
                </div>
              )}
              {showSuccess && addedToCart && addedToCart.offer.price?.discount?.percent && (
                <span
                  {...tDanger('products:YOU_HAVE_EARNED_X_OFF', {
                    percentage: addedToCart.offer.price?.discount?.percent,
                  })}
                />
              )}
            </>
          )
          : (
            <div className={styles.addMore}>
              {!showSuccess && (
                <>
                  {t('products:SAVE_ON_THE_NEXT')}
                  {' '}
                  |
                  {' '}
                  {t('products:ADD_COUNT_MORE', { count: 1 })}
                  <span>{t('products:GET_X_OFF', { percentage })}</span>
                </>
              )}
              {showSuccess && addedToCart && addedToCart.offer.price?.discount?.percent && (
                <span
                  className={styles.successMessage}
                  {...tDanger('products:YOU_HAVE_EARNED_X_OFF', {
                    percentage: addedToCart.offer.price?.discount?.percent,
                  })}
                />
              )}
            </div>
          )}
      </div>
      <div className={styles.list}>
        {!showSuccess && !showProgressBar && (
          <div className={styles.addMore}>
            {!showSuccess && (
              <>
                {t('products:ADD_COUNT_MORE', { count: 1 })}
              </>
            )}
            {showSuccess && addedToCart && addedToCart.offer.price?.discount?.percent && (
              <span
                {...tDanger('products:YOU_HAVE_EARNED_X_OFF', {
                  percentage: addedToCart.offer.price?.discount?.percent,
                })}
              />
            )}
          </div>
        )}
        {map(tactic?.offers || [], tacticOffer => (
          <ProductItem
            key={`AddAndSaveTactic_${tacticOffer.product?.id}`}
            action={(
              <BuyButtonV2
                className={clsx(styles.AddToCartProduct, { [styles.success]: addedToCart?.product?.id === tacticOffer.product?.id })}
                color="black"
                disabled={(addedToCart?.product?.id === tacticOffer.product?.id)}
                onSuccess={() => {
                  setAddedToCart(tacticOffer)
                  setShowSuccess(true)
                }}
                productID={tacticOffer.product?.id || ''}
                products={[
                  {
                    productID: tacticOffer.product?.id || '',
                    quantity: 1,
                    tactic: createAddToCartTacticFromOffer(tacticOffer.offer),
                    parentLineItemID: lineItem.id,
                  },
                ]}
                sourceID={tacticOffer.offer.type || undefined}
                useCache={false}
                withoutSuccessPrompt
              >
                {/* If not added to cart, show plus icon */}
                {addedToCart?.product?.id !== tacticOffer.product?.id && <SvgPlus />}
                {addedToCart && addedToCart?.product?.id === tacticOffer.product?.id && (
                  <SvgCheck />
                )}
              </BuyButtonV2>
            )}
            analytics={{
              source_id: tacticOffer.offer.type || undefined,
              dest_page_type: 'product',
              dest_page_type_id: tacticOffer.product?.id || '',
            }}
            className={styles.productItem}
            content={tacticOffer.product?.name || ''}
            discount={tacticOffer.offer.price?.discount?.format || undefined}
            imageUrl={(tacticOffer.product as any)?.primaryAsset?.url || ''}
            price={tacticOffer.offer.price?.format || ''}
          />
        ))}
      </div>
    </div>
  )
}

